<template>
  <section v-if="enabled" class="when-picker" aria-labelledby="when-picker-title">
    <p id="when-picker-title" class="when-picker__title">¿Cuándo lo quieres?</p>

    <!-- Si el restaurante solo trabaja con pedidos programados no hay elección. -->
    <div v-if="asapEnabled" class="when-picker__modes" role="radiogroup" aria-labelledby="when-picker-title">
      <button
        type="button"
        role="radio"
        class="when-picker__mode"
        :class="{ 'is-active': mode === 'asap' }"
        :aria-checked="mode === 'asap' ? 'true' : 'false'"
        :disabled="!asapAvailable"
        @click="setMode('asap')"
      >
        <i class="fa-solid fa-bolt" aria-hidden="true"></i>
        <span>Lo antes posible</span>
      </button>
      <button
        type="button"
        role="radio"
        class="when-picker__mode"
        :class="{ 'is-active': mode === 'scheduled' }"
        :aria-checked="mode === 'scheduled' ? 'true' : 'false'"
        :disabled="!days.length"
        @click="setMode('scheduled')"
      >
        <i class="fa-regular fa-calendar" aria-hidden="true"></i>
        <span>Otra fecha y hora</span>
      </button>
    </div>

    <p v-if="!asapEnabled" class="when-picker__note when-picker__note--info">
      <i class="fa-regular fa-calendar-check" aria-hidden="true"></i>
      {{ options.asap_reason || 'Este restaurante solo acepta pedidos programados. Elige el día y la hora.' }}
    </p>
    <p v-else-if="!asapAvailable" class="when-picker__note">
      {{ options.asap_reason || 'La cocina está cerrada ahora. Elige cuándo quieres tu pedido.' }}
    </p>

    <div v-if="mode === 'scheduled'" class="when-picker__fields">
      <div class="input-group shadow">
        <span class="input-group-text"><i class="fa-regular fa-calendar-days" aria-hidden="true"></i></span>
        <div class="form-floating flex-grow-1">
          <select id="when-picker-day" v-model="selectedDay" class="form-select" @change="onDayChange">
            <option v-for="day in days" :key="day.date" :value="day.date">{{ day.label }}</option>
          </select>
          <label for="when-picker-day">Día</label>
        </div>
      </div>
      <div class="input-group shadow">
        <span class="input-group-text"><i class="fa-regular fa-clock" aria-hidden="true"></i></span>
        <div class="form-floating flex-grow-1">
          <select id="when-picker-time" v-model="selectedTime" class="form-select" @change="emitValue">
            <option value="" disabled>Elige una hora</option>
            <option v-for="slot in timesForDay" :key="slot.value" :value="slot.value">{{ slot.label }}</option>
          </select>
          <label for="when-picker-time">Hora</label>
        </div>
      </div>
      <p v-if="summary" class="when-picker__summary">
        <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
        {{ kindLabel }} {{ summary }}
      </p>
    </div>

    <p v-if="!asapAvailable && !days.length" class="when-picker__note when-picker__note--error">
      No quedan horas disponibles en los próximos días.
    </p>
  </section>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ScheduledTimePicker',
  props: {
    token: { type: String, default: '' },
    modelValue: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      options: { enabled: false, asap_available: true, days: [] },
      mode: 'asap',
      selectedDay: '',
      selectedTime: '',
    };
  },
  computed: {
    enabled() {
      return Boolean(this.options?.enabled);
    },
    asapEnabled() {
      return this.options?.asap_enabled !== false;
    },
    asapAvailable() {
      return this.asapEnabled && this.options?.asap_available !== false;
    },
    days() {
      return Array.isArray(this.options?.days) ? this.options.days : [];
    },
    timesForDay() {
      return this.days.find((day) => day.date === this.selectedDay)?.times || [];
    },
    kindLabel() {
      return this.options?.kind === 'delivery' ? 'Entrega' : 'Recogida';
    },
    summary() {
      const day = this.days.find((item) => item.date === this.selectedDay);
      const slot = this.timesForDay.find((item) => item.value === this.selectedTime);
      if (!day || !slot) return '';
      const dayText = day.label.charAt(0).toLowerCase() + day.label.slice(1);
      return `${dayText} a las ${slot.label}`;
    },
  },
  mounted() {
    this.loadOptions();
  },
  methods: {
    async loadOptions() {
      if (!this.token) return;
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/v1/api/app/scheduled-orders/options`, {
          headers: { Authorization: `Bearer ${this.token}` },
          timeout: 10000,
        });
        this.options = data || this.options;
      } catch (error) {
        // Sin opciones: el pedido sigue el flujo normal (lo antes posible).
        this.options = { enabled: false, asap_available: true, days: [] };
      }
      this.selectedDay = this.days[0]?.date || '';
      this.selectedTime = '';
      this.mode = this.enabled && !this.asapAvailable ? 'scheduled' : 'asap';
      this.emitValue();
    },
    setMode(mode) {
      this.mode = mode;
      if (mode === 'scheduled' && !this.selectedDay) this.selectedDay = this.days[0]?.date || '';
      this.emitValue();
    },
    onDayChange() {
      if (!this.timesForDay.some((slot) => slot.value === this.selectedTime)) this.selectedTime = '';
      this.emitValue();
    },
    emitValue() {
      const value = this.enabled && this.mode === 'scheduled' ? this.selectedTime : '';
      this.$emit('update:modelValue', value || '');
    },
    // Lo usa el formulario antes de enviar: '' si la elección es válida.
    validationError() {
      if (!this.enabled) return '';
      if (this.mode === 'asap') {
        if (!this.asapEnabled) return 'Este restaurante solo acepta pedidos programados. Elige el día y la hora.';
        return this.asapAvailable ? '' : 'La cocina está cerrada ahora. Elige una fecha y hora.';
      }
      if (!this.days.length) return 'No quedan horas disponibles para programar el pedido.';
      return this.selectedTime ? '' : 'Elige la hora a la que quieres el pedido.';
    },
  },
};
</script>

<style scoped>
.when-picker {
  margin: 4px 0 22px;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fafafa;
}

.when-picker__title {
  margin: 0 0 10px;
  font-weight: 700;
  font-size: 1rem;
  color: #111827;
}

.when-picker__modes {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.when-picker__mode {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  padding: 8px 10px;
  border: 1.5px solid #d1d5db;
  border-radius: 12px;
  background: #fff;
  color: #374151;
  font-weight: 600;
  font-size: 0.92rem;
  line-height: 1.2;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;
}

.when-picker__mode.is-active {
  border-color: var(--primary-color, #f47700);
  background: color-mix(in srgb, var(--primary-color, #f47700) 10%, #fff);
  color: #111827;
}

.when-picker__mode.is-active i {
  color: var(--primary-color, #f47700);
}

.when-picker__mode:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.when-picker__note {
  margin: 10px 0 0;
  font-size: 0.82rem;
  color: #6b7280;
}

.when-picker__note--info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-weight: 600;
}

.when-picker__note--error {
  color: #b91c1c;
}

.when-picker__fields {
  display: grid;
  gap: 12px;
  margin-top: 14px;
}

.when-picker__summary {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: #166534;
}
</style>
